/**
 * Canonical party registry — single source of truth for labels, aliases, codes, and buckets.
 * Keep in sync with backend `backend/src/constants/parties.js`.
 */

export type PartyBucket = 'U' | 'D' | 'R' | 'O';

export interface PartyRegistryEntry {
  id: string;
  displayLabel: string;
  abbrev: string;
  bucket: PartyBucket | null;
  aliases: readonly string[];
  sosCode?: string | null;
  fecCode?: string | null;
  profileSlug?: string | null;
}

export const PARTY_REGISTRY: readonly PartyRegistryEntry[] = [
  {
    id: 'democratic',
    displayLabel: 'Democratic',
    abbrev: 'D',
    bucket: 'D',
    aliases: ['Democrat', 'Democratic', 'DEM', 'D', 'democrat', 'democratic'],
    sosCode: 'DEM',
    fecCode: 'DEM',
    profileSlug: 'democrat',
  },
  {
    id: 'republican',
    displayLabel: 'Republican',
    abbrev: 'R',
    bucket: 'R',
    aliases: ['Republican', 'REP', 'R', 'republican'],
    sosCode: 'REP',
    fecCode: 'REP',
    profileSlug: 'republican',
  },
  {
    id: 'unaffiliated',
    displayLabel: 'Unaffiliated',
    abbrev: 'U',
    bucket: 'U',
    aliases: ['Unaffiliated', 'UAF', 'UNA', 'UN', 'U', 'UNAFFILIATED', 'NPA', 'UNF'],
    sosCode: 'UAF',
    fecCode: 'NPA',
  },
  {
    id: 'independent',
    displayLabel: 'Independent',
    abbrev: 'I',
    bucket: 'O',
    aliases: ['Independent', 'IND', 'I', 'independent'],
    fecCode: 'IND',
    profileSlug: 'independent',
  },
  {
    id: 'libertarian',
    displayLabel: 'Libertarian',
    abbrev: 'L',
    bucket: 'O',
    aliases: ['Libertarian', 'LBR', 'LIB', 'libertarian'],
    sosCode: 'LBR',
    fecCode: 'LIB',
  },
  {
    id: 'green',
    displayLabel: 'Green Party',
    abbrev: 'G',
    bucket: 'O',
    aliases: ['Green Party', 'Green', 'GRN', 'GRE', 'green party', 'green'],
    sosCode: 'GRN',
    fecCode: 'GRE',
  },
  {
    id: 'non_partisan',
    displayLabel: 'Non-Partisan',
    abbrev: 'N',
    bucket: 'O',
    aliases: ['Non-Partisan', 'Non Partisan', 'Nonpartisan', 'non-partisan'],
  },
  {
    id: 'no_labels',
    displayLabel: 'No Labels',
    abbrev: 'N',
    bucket: 'O',
    aliases: ['No Labels', 'NOL', 'no labels'],
    sosCode: 'NOL',
  },
  {
    id: 'american_constitution',
    displayLabel: 'American Constitution',
    abbrev: 'A',
    bucket: 'O',
    aliases: ['American Constitution', 'ACN', 'CON', 'american constitution'],
    sosCode: 'ACN',
    fecCode: 'CON',
  },
  {
    id: 'unity',
    displayLabel: 'Unity',
    abbrev: 'U',
    bucket: 'O',
    aliases: ['Unity', 'Unity Party', 'UNI', 'unity'],
    sosCode: 'UNI',
  },
  {
    id: 'approval_voting',
    displayLabel: 'Approval Voting',
    abbrev: 'A',
    bucket: 'O',
    aliases: ['Approval Voting', 'Approval Voting Party', 'APV', 'approval voting'],
    sosCode: 'APV',
  },
  {
    id: 'center',
    displayLabel: 'Center',
    abbrev: 'C',
    bucket: 'O',
    aliases: ['Center', 'Colorado Center Party', 'Colorado Center', 'CTR', 'center'],
    sosCode: 'CTR',
  },
  {
    id: 'forward',
    displayLabel: 'Forward',
    abbrev: 'F',
    bucket: 'O',
    aliases: ['Forward', 'Colorado Forward Party', 'Forward Party', 'FWD', 'forward'],
    sosCode: 'FWD',
  },
  {
    id: 'unknown',
    displayLabel: 'Unknown',
    abbrev: '',
    bucket: 'O',
    aliases: ['Unknown', 'UNK', 'unknown'],
    fecCode: 'UNK',
  },
] as const;

const PARTY_DISPLAY_ORDER: readonly string[] = [
  'democratic',
  'republican',
  'unaffiliated',
  'non_partisan',
  'libertarian',
  'green',
  'independent',
  'no_labels',
  'american_constitution',
  'unity',
  'approval_voting',
  'center',
  'forward',
  'unknown',
];

export const PARTY_BUCKET_ORDER: readonly PartyBucket[] = ['U', 'D', 'R', 'O'];

export const PARTY_BUCKET_LABELS: Record<PartyBucket, string> = {
  U: 'Unaffiliated',
  D: 'Democratic',
  R: 'Republican',
  O: 'Other',
};

const byId = new Map(PARTY_REGISTRY.map((e) => [e.id, e]));
const aliasIndex = new Map<string, PartyRegistryEntry>();

for (const entry of PARTY_REGISTRY) {
  const keys = new Set<string>([
    entry.id,
    entry.displayLabel,
    ...entry.aliases,
    ...(entry.sosCode ? [entry.sosCode] : []),
    ...(entry.fecCode ? [entry.fecCode] : []),
    ...(entry.profileSlug ? [entry.profileSlug] : []),
  ]);
  for (const key of keys) {
    const norm = String(key || '').trim().toLowerCase();
    if (norm && !aliasIndex.has(norm)) {
      aliasIndex.set(norm, entry);
    }
  }
}

/** Admin/candidate dropdown labels (legacy PARTY_OPTIONS order). */
export const PARTY_DISPLAY_OPTIONS: readonly string[] = PARTY_DISPLAY_ORDER
  .map((id) => byId.get(id)?.displayLabel)
  .filter((label): label is string => Boolean(label));

/** Outreach / SOS demographic codes — stored value is code, label is display. */
export const PARTY_SOS_OPTIONS: readonly { value: string; label: string }[] = PARTY_REGISTRY
  .filter((e) => e.sosCode)
  .map((e) => ({ value: e.sosCode as string, label: e.displayLabel }))
  .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Profile preference dropdown — the full registry, in display order.
 *
 * This used to be `.filter(e => e.profileSlug)`, which offered exactly three
 * parties (Democrat / Republican / Independent) and keyed them by the legacy
 * `profileSlug` rather than the registry id. A member could not record
 * Unaffiliated — the most common affiliation in Colorado — and a stored value
 * from anywhere else in the app ('DEM', 'Unaffiliated', 'democratic') matched
 * no option, so the field fell back to its placeholder (issue #1141).
 *
 * The value is now the registry `id`, consistent with every other party
 * surface. Read stored values through `resolveParty()`, which still maps the
 * old slugs and every alias onto the right entry.
 *
 * 'unknown' is excluded: it is what we infer when data is missing, not
 * something a person chooses for themselves.
 */
export const PARTY_PROFILE_OPTIONS: readonly { value: string; label: string }[] = PARTY_DISPLAY_ORDER
  .map((id) => byId.get(id))
  .filter((e): e is PartyRegistryEntry => Boolean(e) && e!.id !== 'unknown')
  .map((e) => ({ value: e.id, label: e.displayLabel }));

/** Major-party filters (legislators, citizen, vote predictions). */
export const PARTY_MAJOR_FILTER_OPTIONS: readonly { value: string; label: string }[] = [
  { value: 'democratic', label: 'Democratic' },
  { value: 'republican', label: 'Republican' },
  { value: 'independent', label: 'Independent' },
];

export function resolveParty(raw?: string | null): PartyRegistryEntry | null {
  const norm = String(raw ?? '').trim().toLowerCase();
  if (!norm) return null;

  const exact = aliasIndex.get(norm);
  if (exact) return exact;

  const upper = norm.toUpperCase();
  if (upper.includes('INDEPENDENT') || upper === 'IND' || upper === 'I') {
    return byId.get('independent') ?? null;
  }
  if (upper.includes('DEMOCRAT') || upper === 'DEM' || upper === 'D') {
    return byId.get('democratic') ?? null;
  }
  if (upper.includes('REPUBLICAN') || upper === 'REP' || upper === 'R') {
    return byId.get('republican') ?? null;
  }
  if (
    upper.includes('UNAFFILIATED')
    || upper === 'UAF'
    || upper === 'UNA'
    || upper === 'UN'
    || upper === 'U'
    || upper === 'NPA'
    || upper === 'UNF'
  ) {
    return byId.get('unaffiliated') ?? null;
  }

  return null;
}

export function formatPartyDisplay(raw?: string | null): string {
  const trimmed = String(raw ?? '').trim();
  if (!trimmed) return '';
  const entry = resolveParty(trimmed);
  return entry ? entry.displayLabel : trimmed;
}

export function partyFilterMatchValues(filterValue?: string | null): string[] {
  const trimmed = String(filterValue ?? '').trim();
  if (!trimmed) return [];

  const bySlug = byId.get(trimmed.toLowerCase());
  if (bySlug) return collectFilterValues(bySlug);

  const resolved = resolveParty(trimmed);
  if (resolved) return collectFilterValues(resolved);

  return [trimmed];
}

function collectFilterValues(entry: PartyRegistryEntry): string[] {
  const values = new Set<string>([
    entry.displayLabel,
    ...entry.aliases,
    ...(entry.sosCode ? [entry.sosCode] : []),
  ]);
  return [...values].filter(Boolean);
}

export function matchesPartyFilter(raw?: string | null, filterValue?: string | null): boolean {
  const trimmedFilter = String(filterValue ?? '').trim();
  if (!trimmedFilter) return true;

  const filterEntry = resolveParty(trimmedFilter) ?? byId.get(trimmedFilter.toLowerCase()) ?? null;
  const rawEntry = resolveParty(raw);

  if (filterEntry && rawEntry) {
    return filterEntry.id === rawEntry.id;
  }

  const filterVals = partyFilterMatchValues(trimmedFilter);
  const rawNorm = String(raw ?? '').trim().toLowerCase();
  if (!rawNorm) return false;

  return filterVals.some((v) => rawNorm === String(v).trim().toLowerCase());
}

export function formatPartyBucketLabel(key: string): string {
  const bucket = key.trim().toUpperCase() as PartyBucket;
  return PARTY_BUCKET_LABELS[bucket] ?? key;
}
